"use client";

import React, { useState, useEffect, useRef } from "react";
import { Menu, Search, User, X, ShoppingBag, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { ProductsSearch, useWPQueryForSearch } from "@/components/shop/ProductsSearch";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function SearchResults() {
  const {
    products,
    loading,
    error
  } = useEcommerceProducts();
  if (loading) {
    return <div className="p-4 text-sm font-mono text-muted-foreground">Analysing records...</div>;
  }
  if (error) {
    return <div className="p-4 text-sm font-mono text-destructive">System error retrieving data.</div>;
  }
  if (!products || products.length === 0) {
    return <div className="p-4 text-sm font-mono text-muted-foreground">No matching parcels found.</div>;
  }
  return <div data-section-id="20175" className="grid max-h-[60vh] overflow-y-auto p-2 gap-2">
      {products.map((product, i) => <Link key={product.id} to={product.link} className="flex items-center gap-3 rounded-sm p-2 hover:bg-secondary transition-colors group" data-index={i}>
          <div className="h-12 w-12 shrink-0 overflow-hidden rounded-sm bg-muted">
            {product.featured_image_url ? <img src={product.featured_image_url} alt={product.title} className="h-full w-full object-cover grayscale hover:grayscale-0 transition-all" /> : <div className="flex h-full w-full items-center justify-center bg-secondary text-secondary-foreground">
                <ShoppingBag className="h-4 w-4" />
              </div>}
          </div>
          <div className="flex-1 min-w-0">
            <p className="truncate text-sm font-bold uppercase tracking-tight text-foreground group-hover:text-primary">
              {product.title}
            </p>
            <p className="text-xs font-mono text-muted-foreground">
              {product.price ? `Value: $${product.price}` : "View Details"}
            </p>
          </div>
        </Link>)}
    </div>;
}
function SearchWidget({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const [searchValue, setSearchValue] = useState("");
  const [wp_query, hasSearchQuery] = useWPQueryForSearch(searchValue);
  const containerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, onClose]);
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full border-t border-border bg-background shadow-2xl animate-in fade-in slide-in-from-top-2 duration-200 z-50">
      <div className="container mx-auto max-w-3xl py-6 px-4" ref={containerRef}>
        <div className="relative">
          <EcommerceProductsProvider wp_query={wp_query} postType="product">
            <div className="flex items-center gap-2 mb-4">
              <Search className="h-5 w-5 text-primary" />
              <ProductsSearch value={searchValue} onChange={setSearchValue} className="flex-1 font-mono text-sm border-none focus-visible:ring-1 focus-visible:ring-primary" placeholder="Search by state or county..." />
              <Button variant="ghost" size="icon" onClick={onClose} className="hover:text-primary">
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="min-h-[100px]">
              {!hasSearchQuery ? <div className="text-center py-8 text-muted-foreground text-xs font-mono uppercase tracking-widest">
                  Enter location to search inventory
                </div> : <SearchResults />}
            </div>
          </EcommerceProductsProvider>
        </div>
      </div>
    </div>;
}
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) return <div className="h-5 w-24 bg-muted/10 animate-pulse rounded" />;
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="relative text-sm font-medium uppercase tracking-widest text-foreground/80 transition-colors hover:text-primary py-2 font-sans
                  after:absolute after:bottom-0 after:left-1/2 after:h-[1px] after:w-0 after:bg-primary 
                  after:transition-all after:duration-300 hover:after:w-full hover:after:left-0">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 bg-background/98 backdrop-blur-xl md:hidden animate-in fade-in duration-200">
      <div className="flex flex-col h-full p-8">
        <div className="flex items-center justify-between mb-12">
          <span className="text-sm font-mono tracking-widest uppercase text-muted-foreground">Navigation</span>
          <Button variant="ghost" size="icon" onClick={onClose} className="hover:text-primary">
            <X className="h-6 w-6" />
          </Button>
        </div>
        <nav className="flex flex-col gap-8">
          {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-4xl font-bold uppercase tracking-tighter hover:text-primary transition-colors font-sans" onClick={onClose} data-index={i}>
              {item.label}
            </Link>)}
          <Link to="tel:5155285999" className="mt-8 flex items-center gap-3 text-2xl font-mono font-bold text-primary hover:text-primary/80 transition-colors" onClick={onClose}>
            <Phone className="h-6 w-6" />
            (515)-528-5999
          </Link>
        </nav>
      </div>
    </div>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest('a[href]') as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute('href');
        if (!href) return;
        let hash = '';
        if (href.startsWith('#')) {
          hash = href;
        } else if (href.startsWith('/') && href.includes('#')) {
          const [path, hashPart] = href.split('#');
          if (path === '/' || path === window.location.pathname) {
            hash = '#' + hashPart;
          }
        }
        if (hash && hash !== '#') {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: 'smooth',
              block: 'start'
            });
          }
        }
      }
    };
    document.addEventListener('click', handleAnchorClick);
    return () => document.removeEventListener('click', handleAnchorClick);
  }, []);
  return <header id="header" className={`sticky top-0 z-40 w-full border-b border-transparent transition-all duration-300 dark bg-background text-foreground
        ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-xl" : "bg-background"}`}>
      <div className="container mx-auto px-6 h-20 flex items-center justify-center">
        
        <div className="flex-1 flex items-center justify-start">
          <div className="hidden md:block">
            <MenuProvider menu_id="16">
              <DesktopMenu />
            </MenuProvider>
          </div>
          <div className="md:hidden">
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="hover:bg-transparent hover:text-primary">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Open menu</span>
            </Button>
          </div>
        </div>

        <div className="flex-shrink-0 flex justify-center">
          <Link to="/" className="block">
            <Button variant="ghost" className="text-xl font-bold uppercase tracking-tighter text-foreground whitespace-nowrap p-0 h-auto hover:bg-transparent">
              Brentwood Enterprises LLC
            </Button>
          </Link>
        </div>

        <div className="flex-1 flex items-center justify-end gap-2 sm:gap-6">
          <Button variant="ghost" size="icon" onClick={() => setIsSearchOpen(!isSearchOpen)} className="hover:bg-secondary/50 hover:text-primary transition-colors">
            <Search className="h-5 w-5" />
            <span className="sr-only">Search Records</span>
          </Button>

          <Link to="tel:5155285999" className="hidden md:flex items-center gap-2 text-xs font-mono font-bold tracking-widest text-primary hover:text-primary/80 transition-colors uppercase">
            <Phone className="h-4 w-4" />
            (515)-528-5999
          </Link>

          <div className="sm:hidden">
            <Link to="#">
              <Button variant="ghost" size="icon" className="text-primary">
                <User className="h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      <SearchWidget isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />

      <MenuProvider menu_id="16">
        <MobileMenu isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}